import React, { useRef } from "react";
import type { OtcPopupProduct } from "data-types";
import SearchIcon from "../../../icons/Search.svg";
import X from "../../../icons/X.svg";
import * as translator from "../../../utils/translator";
import { getPrice } from "../../../utils/translator";
import { useCartContext } from "../../../customHooks/useCart";
import { clientConfig } from "../../../config";
import type { OtcPopupSearchStatus } from "./useOtcPopupSearch";
import QuantityControl from "../../QuantityControl/QuantityControl";
import { useOtcQuantityControl } from "./useOtcQuantityControl";

type Props = {
  language: string;
  query: string;
  status: OtcPopupSearchStatus;
  results: OtcPopupProduct[];
  onQueryChange: (q: string) => void;
  onFocus: () => void;
  onClear: () => void;
  onResultClick: (product: OtcPopupProduct, index: number) => void;
  onResultAdd: (product: OtcPopupProduct, index: number) => void;
  onResultIncrement: (product: OtcPopupProduct, index: number) => void;
  onResultDecrement: (product: OtcPopupProduct, index: number) => void;
};

type ResultRowProps = Pick<Props, "language" | "onResultClick" | "onResultAdd" | "onResultIncrement" | "onResultDecrement"> & {
  product: OtcPopupProduct;
  index: number;
};

const SearchResultRow: React.FC<ResultRowProps> = ({ product, index, language, onResultClick, onResultAdd, onResultIncrement, onResultDecrement }) => {
  const { cartCurrency } = useCartContext();
  const { quantity, isIncrementDisabled } = useOtcQuantityControl(product);
  return (
    <div className="otc-popup-v3-search-result" data-testid={`otc-popup-v3-search-result-${product.id}`} onClick={() => onResultClick(product, index)}>
      <img src={clientConfig.IMAGES_URL + product.imgSrc} alt="" className="otc-popup-v3-search-result-image" />
      <div className="otc-popup-v3-search-result-details">
        <p className="otc-popup-v3-search-result-title">{product.title}</p>
        {product.custom_quantity_title ? <p className="otc-popup-v3-search-result-content">{translator.translate(language, "shipping", "OTCPopupV3/content_label")}: {product.custom_quantity_title}</p> : null}
        {quantity > 0 ? <p className="otc-popup-v3-search-result-in-cart" data-testid={`otc-popup-v3-search-in-cart-${product.id}`}>✓ {translator.translate(language, "shipping", "OTCPopupV3/in_cart")}</p> : null}
      </div>
      <div className="otc-popup-v3-search-result-price">
        <span>{getPrice(product.price, cartCurrency.locale, cartCurrency.rate)}</span>
        {product.is_discounted && product.percentage_discount !== null ? <span className="otc-popup-v3-search-result-discount">-{product.percentage_discount}%</span> : null}
      </div>
      {quantity > 0 ? (
        <div className="otc-popup-v3-search-quantity" onClick={(e) => e.stopPropagation()}>
          <QuantityControl
            idPrefix={`otc-popup-v3-search-${product.id}-quantity-control`}
            quantity={quantity}
            incrementButtonOnClick={() => onResultIncrement(product, index)}
            decrementButtonOnClick={() => onResultDecrement(product, index)}
            isIncrementDisabled={isIncrementDisabled}
          />
        </div>
      ) : (
        <button type="button" data-testid={`otc-popup-v3-search-add-${product.id}`} className="otc-popup-v3-search-add" disabled={isIncrementDisabled} onClick={(e) => { e.stopPropagation(); onResultAdd(product, index); }}>+</button>
      )}
    </div>
  );
};

const OTCPopupSearch: React.FC<Props> = ({ language, query, status, results, onQueryChange, onFocus, onClear, onResultClick, onResultAdd, onResultIncrement, onResultDecrement }) => {
  const focusedOnce = useRef(false);
  const showDropdown = status === "ok" || status === "empty" || (status === "loading" && results.length > 0);
  return (
    <div className="otc-popup-v3-search">
      <div className={`otc-popup-v3-search-box${query ? " otc-popup-v3-search-box-active" : ""}`}>
        <img src={SearchIcon} alt="" className="otc-popup-v3-search-icon" />
        <input
          id="otc-popup-v3-search-input"
          type="text"
          value={query}
          placeholder={translator.translate(language, "shipping", "OTCPopupV3/search_placeholder")}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => { if (!focusedOnce.current) { focusedOnce.current = true; onFocus(); } }}
          autoComplete="off"
        />
        {query ? <button type="button" data-testid="otc-popup-v3-search-clear" className="otc-popup-v3-search-clear" onClick={onClear}><img src={X} alt="×" /></button> : null}
      </div>
      {showDropdown ? (
        <div className="otc-popup-v3-search-dropdown">
          {status === "empty" ? (
            <div className="otc-popup-v3-search-empty">
              <img src={SearchIcon} alt="" />
              <p className="otc-popup-v3-search-empty-title">{translator.translate(language, "shipping", "OTCPopupV3/no_results_title")}</p>
              <p className="otc-popup-v3-search-empty-text">{translator.translate(language, "shipping", "OTCPopupV3/no_results_text")}</p>
            </div>
          ) : (
            results.map((product, index) => (
              <SearchResultRow
                key={product.id}
                product={product}
                index={index}
                language={language}
                onResultClick={onResultClick}
                onResultAdd={onResultAdd}
                onResultIncrement={onResultIncrement}
                onResultDecrement={onResultDecrement}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

export default OTCPopupSearch;
